import { formatSceneTime, impliedPrecision, isDuration, momentText, parseTimeInput, TIME_PRECISIONS, type MarkPreview, type MarkRole, type TimePrecision } from '@dispositio/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { de } from '../../i18n/de';
import { describeTimeMark } from '../../lib/timeMarkText';

export interface TimeMarkValue {
  value: string;
  precision: TimePrecision | null;
  approx: boolean;
  /** Beginn, Ende oder Dauer der Szene; null: von selbst */
  role: MarkRole | null;
}

interface Props {
  /** die markierte Stelle */
  text: string;
  /** bestehende Zeitmarke bzw. erkannter Vorschlag; null: der Text wurde nicht als Zeit erkannt */
  initial: TimeMarkValue | null;
  /** schon eine Zeitmarke: „Entfernen“ */
  existing: boolean;
  /**
   * Was eine Zeit an dieser Stelle für die Szene ergibt (mit Anker und Zeitmarken davor); null: der Text bestimmt keine Szenenzeit
   * (kein Draft einer Szene)
   */
  preview: ((value: string) => MarkPreview) | null;
  /** Titel einer Szene (für „gerechnet ab …“) */
  sceneTitle: (sceneId: string) => string;
  anchor: { left: number; top: number; bottom: number };
  onApply: (v: TimeMarkValue) => void;
  onRemove: () => void;
  /** `back`: mit Esc bzw. ✕ geschlossen – der Cursor kehrt an die Stelle zurück */
  onClose: (back: boolean) => void;
}

const WIDTH = 380;
const T = de.timeMarks;

/**
 * Zeitmarke setzen bzw. ändern (PLAN.md §16, §17): die Zeit als Datum, relativ oder in Worten (bzw. eine Dauer); darunter, was sie
 * bedeutet und welche Zeit die Szene dadurch bekommt (bzw. warum keine und was zu tun ist), dazu die Rolle (Beginn, Ende, Dauer),
 * Genauigkeit und „ungefähr“. Vorbelegt mit der bestehenden Marke bzw. dem, was im markierten Text erkannt wurde. Wurde nichts erkannt,
 * bleibt das Feld leer: Der Dialog sagt das und zeigt die Formate mit anklickbaren Beispielen – der Text des Autors bleibt, wie er ist.
 */
export function TimeMarkEditor({ text, initial, existing, preview, sceneTitle, anchor, onApply, onRemove, onClose }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const unrecognized = initial === null;
  const [input, setInput] = useState(initial?.value ?? '');
  const parsed = useMemo(() => parseTimeInput(input), [input]);
  const [precision, setPrecision] = useState<TimePrecision | null>(initial?.precision ?? null);
  const [approx, setApprox] = useState(initial?.approx ?? false);
  const [role, setRole] = useState<MarkRole | null>(initial?.role ?? null);
  // eine neue Eingabe bringt ihre Genauigkeit und „ungefähr“ mit (die Wahl darunter überschreibt sie)
  const touched = useRef(false);
  useEffect(() => {
    if (touched.current || !parsed || input === initial?.value) return;
    setPrecision(parsed.precision);
    setApprox(parsed.approx);
    setRole(parsed.role);
  }, [parsed, input, initial?.value]);
  useEffect(() => {
    const onDown = (e: MouseEvent): void => {
      if (host.current && !host.current.contains(e.target as Node)) onClose(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [onClose]);

  const apply = (): void => {
    // eine Dauer ist immer eine Dauer
    if (parsed) onApply({ value: parsed.value, precision, approx, role: isDuration(parsed.value) ? 'duration' : role === 'duration' ? null : role });
  };
  const takeExample = (example: string): void => {
    setInput(example);
    field.current?.focus();
  };
  const left = Math.max(8, Math.min(anchor.left, window.innerWidth - WIDTH - 8));
  const tall = unrecognized ? 470 : 300;
  const top = anchor.bottom + 8 + tall > window.innerHeight ? Math.max(8, anchor.top - tall - 8) : anchor.bottom + 8;
  const short = text.length > 40 ? `${text.slice(0, 38)}…` : text;

  // was die Zeit für die Szene ergibt
  const result = parsed && preview ? preview(parsed.value) : null;
  const sceneLine =
    result === null
      ? null
      : result.moment
        ? {
            tone: 'ok' as const,
            text: (result.anchorId ? (v: string) => T.sceneGetsFrom(v, sceneTitle(result.anchorId!)) : T.sceneGets)(formatSceneTime(momentText(result.moment), { precision: precision ?? impliedPrecision(parsed?.value ?? ''), approx })),
          }
        : result.reason === 'duration'
          ? null
          : { tone: 'warn' as const, text: T.noSceneTime[result.reason] };

  return (
    <div
      ref={host}
      className="time-editor"
      style={{ left, top, width: WIDTH }}
      role="dialog"
      aria-label={T.title(short)}
      data-testid="time-editor"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onClose(true);
        } else if (e.key === 'Enter') {
          e.preventDefault();
          apply();
        }
      }}
    >
      <div className="te-head">
        <strong className="grow">{T.title(short)}</strong>
        <button type="button" className="btn ghost icon" onClick={() => onClose(true)} aria-label={de.common.close}>
          ✕
        </button>
      </div>
      {unrecognized && (
        <p className="te-notice" data-testid="time-unrecognized">
          {T.unrecognized(short)}
        </p>
      )}
      <label className="col">
        <span className="small">{T.input}</span>
        <input ref={field} autoFocus value={input} onChange={(e) => setInput(e.target.value)} placeholder={T.inputPlaceholder} title={T.inputHint} data-testid="time-input" />
      </label>
      {parsed ? (
        <div className="small muted" data-testid="time-means">
          {T.means(describeTimeMark(parsed.value, precision, approx, role))}
        </div>
      ) : (
        <>
          {input.trim() !== '' && (
            <div className="small danger-text" data-testid="time-means">
              {T.notUnderstood(input.trim())}
            </div>
          )}
          <div className="te-formats" data-testid="time-formats">
            <div className="small muted">{T.formatsTitle}</div>
            <ul>
              {T.formats.map((f) => (
                <li key={f.label}>
                  <span className="small">{f.label}:</span>
                  <span className="te-examples">
                    {f.examples.map((ex) => (
                      <button key={ex} type="button" className="rb-chip" onClick={() => takeExample(ex)} data-testid="time-example">
                        {ex}
                      </button>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
      {parsed && sceneLine && (
        <div className={`small te-scene ${sceneLine.tone}`} data-testid="time-scene">
          {sceneLine.text}
        </div>
      )}
      {parsed && !preview && (
        <div className="small muted" data-testid="time-scene">
          {T.notDraft}
        </div>
      )}
      <div className="seg" role="group" aria-label={T.role} title={T.roleHint}>
        {([null, 'start', 'end'] as const).map((r) => (
          <button
            key={r ?? 'auto'}
            type="button"
            className={`seg-btn${role === r && !(parsed && isDuration(parsed.value)) ? ' active' : ''}`}
            aria-pressed={role === r}
            disabled={!!parsed && isDuration(parsed.value)}
            onClick={() => {
              touched.current = true;
              setRole(r);
            }}
            data-testid={`time-role-${r ?? 'auto'}`}
          >
            {r ? T.roles[r] : T.roleAuto}
          </button>
        ))}
      </div>
      <div className="row wrap">
        <label className="row" title={de.sceneTime.precisionHint}>
          {de.sceneTime.precision}
          <select
            value={precision ?? ''}
            onChange={(e) => {
              touched.current = true;
              setPrecision((e.target.value || null) as TimePrecision | null);
            }}
            data-testid="time-precision"
          >
            <option value="">{T.precisionAuto}</option>
            {[...TIME_PRECISIONS].reverse().map((p) => (
              <option key={p} value={p}>
                {de.sceneTime.precisions[p]}
              </option>
            ))}
          </select>
        </label>
        <label className="row" title={de.sceneTime.approxHint}>
          <input
            type="checkbox"
            checked={approx}
            onChange={(e) => {
              touched.current = true;
              setApprox(e.target.checked);
            }}
            data-testid="time-approx"
          />
          {de.sceneTime.approx}
        </label>
      </div>
      <div className="row te-actions">
        {existing && (
          <button type="button" className="btn small ghost danger-text" onClick={onRemove} data-testid="time-remove">
            {T.remove}
          </button>
        )}
        <span className="grow" />
        <button type="button" className="btn small primary" disabled={!parsed} onClick={apply} data-testid="time-apply">
          {T.apply}
        </button>
      </div>
    </div>
  );
}
